// SymptomCheckerWizard.jsx
// Flow: Welcome → Consent → IdentifyPatient → PreScreen → Symptoms → Cluster
//       → Questionnaire → ScreeningQuestions → Triage → Results
//
// WelcomeScreen, ConsentScreen, IdentifyPatientScreen are now shared with
// CareNavigator — imported from CareNavigatorScreens.jsx.
// The `setProfile` prop on IdentifyPatientScreen replaces the old `onProfileSave`
// callback; internally CareNavigatorScreens calls setProfile with the same shape.

import { useState } from "react";
import styles from "./SymptomCheckerWizard.module.css";

// ── Shared screens (previously inline, now from CareNavigator) ────────────────
import {
  WelcomeScreen,
  // ConsentScreen,
  IdentifyPatientScreen,
} from "../Shared/CommonProfileScreen"; // adjust path to match your project

// ── Wizard-specific steps ────────────────────────────────────────────────────
import StepPreScreen from "./StepPreScreen";
import StepSelectSymptoms from "./StepSelectSymptoms";
import StepStatements from "./StepStatements";
import StepQuestionnaire from "./StepQuestionnaire";
import StepTriage from "./StepTriage";
import StepSymptomsCluster from "./StepSymptomsCluster";
import StepScreeningQuestions from "./StepScreeningQuestions";
import StepResults from "./StepResults";
import useCurrentLocation from "../../hooks/useCurrentLocation";
import ConsentScreen from "../Reusablecomponents/Consentscreen/Consentscreen";
import { WEIGHT_DEFAULT_KG,HEIGHT_DEFAULT_IN } from "../../config/constants";
// import { HEIGHT_DEFAULT_IN, WEIGHT_DEFAULT_KG } from "../CareJourney/Heightweightscreen";

// ── Step config ───────────────────────────────────────────────────────────────
const STEPS = [
  { id: "welcome", label: "Welcome", icon: "👋" },
  { id: "consent", label: "Consent", icon: "✅" },
  { id: "identify", label: "Identity", icon: "🧍" },
  { id: "prescreen", label: "Pre-Screen", icon: "📋" },
  { id: "symptoms", label: "Symptoms", icon: "🔍" },
  { id: "cluster", label: "Cluster", icon: "🗂️" },
  { id: "questions", label: "Questions", icon: "❓" },
  { id: "screening", label: "Screening", icon: "🩺" },
  { id: "triage", label: "Analysis", icon: "⚡" },
  { id: "results", label: "Results", icon: "👨‍⚕️" },
];

// ── Default patient (overridden once IdentifyPatientScreen completes) ─────────
const DEFAULT_PATIENT = {
  name: "Priya Sharma",
  gender: "Female",
  age: 32,
  location: "Hyderabad, India",
  weightKg: WEIGHT_DEFAULT_KG,
  heightCm: HEIGHT_DEFAULT_IN,
  bloodGroup: "B+",
  avatarUrl: null,
  history: {
    overweightOrObese: true,
    hypertension: false,
    cholesterol: false,
    smoking: "unknown",
    recentInjury: "unknown",
  },
};

// ── Helper: convert profile saved by IdentifyPatientScreen → patient shape ────
function profileToPatient(profileData) {
  return {
    name: profileData.fullName,
    gender: "Female",
    age: parseInt(profileData.ageGroup?.split("–")?.[0] || "30", 10),
    location: "Hyderabad, India",
    weightKg: profileData.weight,
    heightCm: profileData.height,
    bloodGroup: "—",
    avatarUrl: null,
    history: {
      overweightOrObese: profileData.weight / ((profileData.height / 100) ** 2) >= 25,
      hypertension: false,
      cholesterol: false,
      smoking: "unknown",
      recentInjury: "unknown",
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
export default function SymptomCheckerWizard({ onClose }) {
  const location = useCurrentLocation();

  const [activeStep, setActiveStep] = useState(0);
  const [patient, setPatientState] = useState(DEFAULT_PATIENT);
  const [wizardData, setWizardData] = useState({
    welcome: null,
    consent: null,
    identity: null,
    prescreen: null,
    symptoms: [],
    statements: [],
    questionnaire: null,
    screening: null,
    triage: null,
    identityState: null,
  });

  const goNext = () => setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const saveAndNext = (key, value) => {
    setWizardData((prev) => ({ ...prev, [key]: value }));
    goNext();
  };

  // Called by IdentifyPatientScreen via the `setProfile` prop.
  // Converts the raw profile object into the patient shape used by later steps.
  const handleSetProfile = (profileData) => {
    setPatientState(profileToPatient(profileData));
  };

  const [identityReturnStep, setIdentityReturnStep] = useState(0);
  // ── Step renderer ──────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (activeStep) {

      /* 0 — Welcome */
      case 0:
        return (
          <WelcomeScreen
            onStart={() => saveAndNext('welcome', true)}
            config={{
              title: 'Check Your Symptoms',
              subtitle: 'Answer a few simple questions and get personalised health guidance.',
              checklist: ['Private & secure', 'Takes < 5 minutes', 'Matched with right experts'],
              ctaLabel: 'Start Symptom Check',
              backUrl: '/',
            }}
          />
        );

      /* 1 — Consent */
      case 1:
        return (
          <div className={styles.page}>
            <div className={styles.card}>
              <ConsentScreen
                onNext={() => saveAndNext('consent', true)}
                onBack={goBack}
                config={{
                  pageTitle: 'Consent & Privacy',
                  pageSubtitle:
                    'Before we personalise your symptom check, please review how your information will be used.',

                  requiredItems: [
                    {
                      id: 'carePersonalization',
                      icon: '🤝',
                      title: 'Symptoms Personalization',
                      description:
                        'Use my answers to personalise my symptom check.',
                    },
                    {
                      id: 'clinicAccess',
                      icon: '👥',
                      title: 'Clinic Care Team Access',
                      description:
                        "Allow the selected clinic's care team to view my submitted information.",
                    },
                    {
                      id: 'privacyNotice',
                      icon: '📋',
                      title: 'Privacy Notice',
                      description:
                        'I have read and understand the privacy notice.',
                    },
                  ],

                  "sideCard": {
                    "title": "Your privacy is our priority",
                    "description": "We collect only what's necessary to provide safe, effective, and personalized care for you.",
                    "items": [
                      { "icon": "🛡️", "title": "Secure & Encrypted", "desc": "Your data is encrypted and stored securely." },
                      { "icon": "🔒", "title": "Private by Design", "desc": "We limit access to your data to authorized care team members only." },
                      { "icon": "👤", "title": "You're in Control", "desc": "You can manage or withdraw optional consent anytime." }
                    ],
                    "infoBubble": "You can manage optional consent later from your profile.",
                    "ctaLabel": "Continue Securely"
                  },
                }}
              />
            </div>
          </div>
        );


      case 2:
        return (
          <IdentifyPatientScreen
            onNext={() => saveAndNext("identity", true)}
            onBack={goBack}
            setProfile={handleSetProfile}
            identifyState={wizardData.identityState}
            setIdentifyState={(updater) => {
              setWizardData((prev) => ({
                ...prev,
                identityState:
                  typeof updater === "function"
                    ? updater(prev.identityState)
                    : updater,
              }));
            }}
            config={{
              pageTitle: "Create Your Profile",
              pageSubtitle:
                "Let's set up your profile so we can personalise your symptom check.",
            }}
          />
        );

      case 3:
        return (
          <div className={styles.page}>
            <div className={styles.card}>
              <StepPreScreen
                patient={patient}
                initial={wizardData.prescreen}
                onNext={(data) => saveAndNext("prescreen", data)}
                onBack={() => {
                  setIdentityReturnStep(3);  // ← set BEFORE goBack
                  goBack();
                }}
                
              />
            </div>
          </div>
        );
      /* 4 — Select Symptoms */
      case 4:
        return (
          <StepSelectSymptoms
            initial={wizardData.symptoms}
            patient={patient}
            onNext={(data) => saveAndNext("symptoms", data)}
            onBack={goBack}
            currentlocation={location}
          />
        );

      /* 5 — Symptoms Cluster */
      case 5:
        return (
          <StepSymptomsCluster
            patient={patient}
            symptoms={wizardData.symptoms}
            onNext={(data) => saveAndNext("statements", data)}
            onBack={goBack}
          />
        );

      /* 6 — Questionnaire */
      case 6:
        return (
          <StepQuestionnaire
            symptoms={wizardData.symptoms}
            initial={wizardData.questionnaire}
            patient={patient}
            onNext={(data) => saveAndNext("questionnaire", data)}
            onBack={goBack}
          />
        );

      /* 7 — Screening Questions (PHQ-9 / GAD-7 / dynamic) */
      case 7:
        return (
          <StepScreeningQuestions
            patient={patient}
            symptoms={wizardData.symptoms}
            initial={wizardData.screening}
            onNext={(data) => saveAndNext("screening", data)}
            onBack={goBack}
          />
        );

      /* 8 — Triage */
      case 8:
        return (
          <StepTriage
            wizardData={wizardData}
            onNext={(data) => saveAndNext("triage", data)}
            onBack={goBack}
          />
        );

      /* 9 — Results */
      case 9:
        return (
          <StepResults
            wizardData={wizardData}
            patient={patient}
            onRestart={() => {
              setActiveStep(0);
              setPatientState(DEFAULT_PATIENT);
              setWizardData({
                welcome: null, consent: null, identity: null,
                prescreen: null, symptoms: [], statements: [],
                questionnaire: null, screening: null, triage: null,
              });
            }}
            onClose={onClose}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {renderStep()}
      </div>
    </div>
  );
}



// // SymptomCheckerWizard.jsx
// // Flow: PreScreen → Symptoms → Cluster → Questionnaire → ScreeningQuestions → Triage → Results
// import { useState } from "react";
// import styles from "./SymptomCheckerWizard.module.css";

// import StepPreScreen          from "./StepPreScreen";
// import StepSelectSymptoms     from "./StepSelectSymptoms";
// import StepStatements         from "./StepStatements";
// import StepQuestionnaire      from "./StepQuestionnaire";
// import StepTriage             from "./StepTriage";
// import StepSymptomsCluster    from "./StepSymptomsCluster";
// import StepScreeningQuestions from "./StepScreeningQuestions";
// import StepResults            from "./StepResults";
// import useCurrentLocation     from "../../hooks/useCurrentLocation";

// // ─── Step config ─────────────────────────────────────────
// const STEPS = [
//   { id: "prescreen",  label: "Pre-Screen", icon: "📋" },
//   { id: "symptoms",   label: "Symptoms",   icon: "🔍" },
//   { id: "cluster",    label: "Cluster",    icon: "🗂️" },
//   { id: "questions",  label: "Questions",  icon: "❓" },
//   { id: "screening",  label: "Screening",  icon: "🩺" },
//   { id: "triage",     label: "Analysis",   icon: "⚡" },
//   { id: "results",    label: "Results",    icon: "👨‍⚕️" },
// ];

// // ─── Patient (static demo) ───────────────────────────────
// const PATIENT = {
//   name: "Priya Sharma",
//   gender: "Female",
//   age: 32,
//   location: "Hyderabad, India",
//   weightKg: 58,
//   heightCm: 163,
//   bloodGroup: "B+",
//   avatarUrl: null,
//   history: {
//     overweightOrObese: true,
//     hypertension: false,
//     cholesterol: false,
//     smoking: "unknown",
//     recentInjury: "unknown",
//   },
// };

// // ─────────────────────────────────────────────────────────
// export default function SymptomCheckerWizard({ onClose }) {
//   const location = useCurrentLocation();

//   const [activeStep, setActiveStep] = useState(0);
//   const [wizardData, setWizardData] = useState({
//     prescreen:    null,
//     symptoms:     [],
//     statements:   [],
//     questionnaire: null,
//     // screening holds { skipped, assessments, answers, results, scoreData }
//     screening:    null,
//     triage:       null,
//   });

//   const goNext = () => setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
//   const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));

//   const saveAndNext = (key, value) => {
//     setWizardData((prev) => ({ ...prev, [key]: value }));
//     goNext();
//   };

//   // ── Step renderer ────────────────────────────────────────
//   const renderStep = () => {
//     switch (activeStep) {

//       /* 0 — Pre-Screen */
//       case 0:
//         return (
//           <StepPreScreen
//             patient={PATIENT}
//             initial={wizardData.prescreen}
//             onNext={(data) => saveAndNext("prescreen", data)}
//           />
//         );

//       /* 1 — Select Symptoms */
//       case 1:
//         return (
//           <StepSelectSymptoms
//             initial={wizardData.symptoms}
//             patient={PATIENT}
//             onNext={(data) => saveAndNext("symptoms", data)}
//             onBack={goBack}
//             currentlocation={location}
//           />
//         );

//       /* 2 — Symptoms Cluster */
//       case 2:
//         return (
//           <StepSymptomsCluster
//             patient={PATIENT}
//             symptoms={wizardData.symptoms}
//             onNext={(data) => saveAndNext("statements", data)}
//             onBack={goBack}
//           />
//         );

//       /* 3 — Questionnaire */
//       case 3:
//         return (
//           <StepQuestionnaire
//             symptoms={wizardData.symptoms}
//             initial={wizardData.questionnaire}
//             patient={PATIENT}
//             onNext={(data) => saveAndNext("questionnaire", data)}
//             onBack={goBack}
//           />
//         );

//       /* 4 — Screening Questions (PHQ-9 / GAD-7 / dynamic) */
//       case 4:
//         return (
//           <StepScreeningQuestions
//             patient={PATIENT}
//             symptoms={wizardData.symptoms}          // ← passed so assessments are matched
//             initial={wizardData.screening}
//             onNext={(data) => saveAndNext("screening", data)}
//             onBack={goBack}
//           />
//         );

//       /* 5 — Triage (animated analysis + result) */
//       case 5:
//         return (
//           <StepTriage
//             wizardData={wizardData}
//             onNext={(data) => saveAndNext("triage", data)}
//             onBack={goBack}
//           />
//         );

//       /* 6 — Results */
//       case 6:
//         return (
//           <StepResults
//             wizardData={wizardData}
//             patient={PATIENT}
//             onRestart={() => {
//               setActiveStep(0);
//               setWizardData({
//                 prescreen: null, symptoms: [], statements: [],
//                 questionnaire: null, screening: null, triage: null,
//               });
//             }}
//             onClose={onClose}
//           />
//         );

//       default:
//         return null;
//     }
//   };

//   return (
//     <div className={styles.root}>
//       <div className={styles.content}>
//         {renderStep()}
//       </div>
//     </div>
//   );
// }
